/**
 * Attachment Storage Utility using IndexedDB
 * Handles storing and retrieving attachment files for persistence across page refreshes
 */

const DB_NAME = "AttachmentStorageDB";
const DB_VERSION = 1;
const STORE_NAME = "attachments";

class AttachmentStorage {
  constructor() {
    this.db = null;
    this.init();
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
          store.createIndex("timestamp", "timestamp", { unique: false });
          store.createIndex("elementId", "elementId", { unique: false });
        }
      };
    });
  }

  async storeAttachments(elementId, files, shortDescription, removedAttachmentIds = []) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      const data = {
        id: elementId,
        elementId: elementId,
        files: files,
        shortDescription: shortDescription,
        removedAttachmentIds: removedAttachmentIds,
        timestamp: Date.now(),
      };

      const request = store.put(data);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error("IndexedDB: Error storing attachments:", request.error);
        reject(request.error);
      };
    });
  }

  async getAttachments(elementId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(elementId);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            files: result.files || [],
            shortDescription: result.shortDescription || "",
            removedAttachmentIds: result.removedAttachmentIds || [],
          });
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error(
          "IndexedDB: Error retrieving attachments:",
          request.error
        );
        reject(request.error);
      };
    });
  }

  async deleteAttachments(elementId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(elementId);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error("IndexedDB: Error deleting attachments:", request.error);
        reject(request.error);
      };
    });
  }

  async clearAllAttachments() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error("IndexedDB: Error clearing attachments:", request.error);
        reject(request.error);
      };
    });
  }

  async getAllAttachments() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        console.error(
          "IndexedDB: Error getting all attachments:",
          request.error
        );
        reject(request.error);
      };
    });
  }
}

// Create a singleton instance
const attachmentStorage = new AttachmentStorage();

export default attachmentStorage;
